import React, { useState, useEffect } from 'react';
import { Typography, withStyles, TextField, Checkbox, FormControlLabel, makeStyles, Button } from '@material-ui/core';
import Modal from '../../../common/Modal';
import { SaveTemplateStyles } from './styles';
import { PropTypes } from 'prop-types';
import LoadingScreen from './../../../../collums-components/components/common/loadingScreen';
import { toastr } from 'react-redux-toastr';
import TreatmentNoteApi from './../../../../collums-components/api/TreatmentNoteApi';
import SnippetsApi from './../../../../collums-components/api/SnippetsApi';
import PractitionerApi from './../../../../collums-components/api/PractitionerApi';
import AuthApi from './../../../../collums-components/api/AuthApi';
import { links } from './../../../../collums-components/api/config';
import { modalsButtonStyles } from '../../../../collums-constants/styles/stylesheets/buttonsStyles';
import Autocomplete from '@material-ui/lab/Autocomplete';

const ALL_PRACTITIONERS_OPTION = { id: '__all__', displayName: 'All practitioners' };

const getLoggedPractitionerId = () => {
    try {
        const token = localStorage.getItem('token');
        if (!token) return null;
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload._id;
    } catch {
        return null;
    }
};

const SaveTemplate = ({ classes, content, closeModal, loadTemplates, type, setTemplatesToxin }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [name, setName] = useState('');
    const [allPractitioners, setAllPractitioners] = useState(false);
    const [addNewPractitioners, setAddNewPractitioners] = useState(false);
    const [selectedPractitioners, setSelectedPractitioners] = useState([]);
    const [practitionerOptions, setPractitionerOptions] = useState([]);
    const globalStyles = makeStyles(modalsButtonStyles)();

    useEffect(() => {
        if (type === 'note') {
            PractitionerApi.listActivePractitioners().then(practitioners => {
                setPractitionerOptions(practitioners);
                const loggedId = getLoggedPractitionerId();
                if (loggedId) {
                    const current = practitioners.find(p => p.id === loggedId);
                    if (current) setSelectedPractitioners([current]);
                }
            });
        }
    }, [type]);

    const save = async () => {
        if (!name) {
            return toastr.error('Missing template name');
        }
        try {
            const margin = type === 'toxin' ? 10 : 11;
            setIsLoading(true);
            const contentToSave = { ...content };
            if (type !== 'note') {
                contentToSave.circles = contentToSave.circles.map(el => ({
                    ...el,
                    top: el.top - margin,
                    left: el.left - margin
                }));
                contentToSave.squares = contentToSave.squares.map(el => ({
                    ...el,
                    top: el.top - margin,
                    left: el.left - margin
                }));
            }

            if (type === 'note') {
                const practitioners = allPractitioners
                    ? practitionerOptions.map(p => p.id)
                    : selectedPractitioners.map(p => p.id);

                await SnippetsApi.create({
                    name,
                    note: contentToSave.note,
                    practitioners,
                    addNewPractitioners
                });
            } else {
                const newTemplate = await TreatmentNoteApi.createTemplate(type, {
                    ...contentToSave,
                    name,
                    allPractitioners
                });
                if (setTemplatesToxin) {
                    setTemplatesToxin(prev => {
                        prev.push(newTemplate);
                        return prev;
                    });
                }
            }
            toastr.success(`${type === 'note' ? 'Snippet' : 'Template'} successfully saved`);
            await loadTemplates();
            closeModal();
        } catch (err) {
            return toastr.error(err?.data?.message || 'Something went wrong (code: c0051)');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            {isLoading && <LoadingScreen />}
            <Modal hideTitle id="save-template" isOpen size="xs" onCancel={closeModal} hideCancelButton>
                <Typography variant="h4" className={`${classes.modalTitle} ${classes.defaultFont}`}>
                    Save new {type === 'note' ? 'snippet' : 'template'}
                </Typography>
                <TextField
                    id="template-name"
                    required
                    label="Name"
                    value={name}
                    onChange={({ target }) => {
                        setName(target.value);
                    }}
                    variant="outlined"
                    fullWidth
                />
                {type === 'note' ? (
                    <>
                        <Autocomplete
                            multiple
                            options={[ALL_PRACTITIONERS_OPTION, ...practitionerOptions]}
                            getOptionLabel={option => option.displayName || `${option.firstName} ${option.lastName}`}
                            value={allPractitioners ? practitionerOptions : selectedPractitioners}
                            onChange={(_, value) => {
                                const hasAllOption = value.some(v => v.id === '__all__');
                                if (hasAllOption) {
                                    setAllPractitioners(true);
                                    setSelectedPractitioners(practitionerOptions);
                                } else {
                                    if (allPractitioners) setAllPractitioners(false);
                                    setSelectedPractitioners(value);
                                }
                            }}
                            getOptionDisabled={option => allPractitioners && option.id !== '__all__'}
                            renderOption={option => (
                                <span>{option.displayName || `${option.firstName} ${option.lastName}`}</span>
                            )}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    label="Choose practitioner access"
                                    style={{ marginTop: 12 }}
                                />
                            )}
                            style={{ marginTop: 4 }}
                        />
                        <FormControlLabel
                            style={{ paddingTop: 5 }}
                            control={
                                <Checkbox
                                    checked={addNewPractitioners}
                                    onChange={(e, value) => {
                                        setAddNewPractitioners(value);
                                    }}
                                />
                            }
                            label={<span style={{ fontSize: '14px' }}>Automatically assign to new practitioners</span>}
                        />
                        <Typography style={{ fontSize: '14px', marginTop: 4 }}>
                            <span
                                role="button"
                                tabIndex={0}
                                style={{ color: '#1a73e8', cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={async () => {
                                    try {
                                        const me = await AuthApi.getMe();
                                        const hasAdminAccess = me.role === 'admin' || me.job?.permissions?.admin;
                                        if (hasAdminAccess) {
                                            window.open(
                                                `${links.adminUrl}/organisation/snippets?token=${localStorage.getItem(
                                                    'token'
                                                )}`,
                                                '_blank'
                                            );
                                        } else {
                                            toastr.error(
                                                'You do not have access to Admin. Please contact your account owner for access.'
                                            );
                                        }
                                    } catch {
                                        toastr.error(
                                            'You do not have access to Admin. Please contact your account owner for access.'
                                        );
                                    }
                                }}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') e.target.click();
                                }}
                            >
                                Snippet manager
                            </span>{' '}
                            (Admin access only)
                        </Typography>
                    </>
                ) : (
                    <FormControlLabel
                        style={{ paddingTop: 5 }}
                        control={
                            <Checkbox
                                checked={allPractitioners}
                                onChange={(e, value) => {
                                    setAllPractitioners(value);
                                }}
                            />
                        }
                        label={<span style={{ fontSize: '14px' }}>Save for all practitioners</span>}
                    />
                )}
                <div className={globalStyles.buttonsContainer}>
                    <Button
                        id="confirm-exit-modal-back-button"
                        variant="outlined"
                        onClick={closeModal}
                        className={globalStyles.cancelButton}
                    >
                        Cancel
                    </Button>
                    <Button
                        id="confirm-exit-modal-continue-button"
                        onClick={save}
                        className={globalStyles.confirmButton}
                    >
                        Save
                    </Button>
                </div>
            </Modal>
        </>
    );
};

SaveTemplate.propTypes = {
    classes: PropTypes.object.isRequired,
    content: PropTypes.object.isRequired,
    closeModal: PropTypes.func.isRequired,
    loadTemplates: PropTypes.func.isRequired,
    setTemplatesToxin: PropTypes.func,
    type: PropTypes.string.isRequired
};

export default withStyles(SaveTemplateStyles)(SaveTemplate);
